import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Briefcase, ChevronDown } from 'lucide-react';
import SkeletonLoader from 'components/ui/SkeletonLoader';
import NoProfile from 'assets/images/NoProfile.png';
import { apiService } from 'services/apiService';
import './StatusReasonModal.css';

interface JobOption {
  id: number;
  title: string;
}

interface StatusReasonModalProps {
  show: boolean;
  initialStatus: string;
  candidateName: string;
  candidateLocation?: string;
  candidateAvatar?: string;
  candidateRole?: string;
  /** Pre-selected job position id — if provided the selector is pre-filled */
  preselectedJobId?: number | null;
  preselectedJobTitle?: string;
  lockJobSelection?: boolean;
  onConfirm: (status: string, reason: string, interviewDate?: string, jobPositionId?: number) => Promise<void>;
  onCancel: () => void;
}

const StatusReasonModal: React.FC<StatusReasonModalProps> = ({
  show,
  initialStatus,
  candidateName,
  candidateLocation,
  candidateAvatar,
  candidateRole,
  preselectedJobId,
  preselectedJobTitle,
  lockJobSelection = false,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | ''>('');
  const [jobError, setJobError] = useState(false);

  const isScheduled = initialStatus === 'Scheduled';

  /* ── Fetch jobs when modal opens ── */
  useEffect(() => {
    if (!show) return;

    // Pre-fill if a job was already known
    setSelectedJobId(preselectedJobId ?? '');
    setJobError(false);
    setReason('');
    setInterviewDate('');

    if (lockJobSelection && preselectedJobId) {
      setJobs(preselectedJobTitle ? [{ id: preselectedJobId, title: preselectedJobTitle }] : []);
      return;
    }

    const fetchJobs = async () => {
      setJobsLoading(true);
      try {
        const res = await apiService.getLookigFor() as any;
        const raw = res?.data;
        const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.jobs) ? raw.jobs : [];
        const mapped: JobOption[] = list.map((j: any) => ({
          id: j.job_position_id ?? j.job_id ?? j.id,
          title: j.designation_name || j.job_title || j.title || 'Untitled Job',
        }));
        setJobs(mapped);
      } catch {
        setJobs([]);
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobs();
  }, [show, preselectedJobId, preselectedJobTitle, lockJobSelection]);

  const handleConfirm = async () => {
    // Validate job selection
    if (!hasValidJobSelection) {
      setJobError(true);
      return;
    }
    if (isScheduled && !interviewDate) return;

    setLoading(true);
    try {
      await onConfirm(
        initialStatus,
        reason,
        isScheduled ? interviewDate : undefined,
        Number(selectedJobId),
      );
    } finally {
      setLoading(false);
      setReason('');
      setInterviewDate('');
      setSelectedJobId('');
      setJobError(false);
    }
  };

  const handleCancel = () => {
    setReason('');
    setInterviewDate('');
    setSelectedJobId('');
    setJobError(false);
    onCancel();
  };

  const minDateTime = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

  const hasValidJobSelection = lockJobSelection
    ? !!selectedJobId
    : !!selectedJobId && jobs.some((job) => job.id === Number(selectedJobId));
  const canConfirm = hasValidJobSelection && (!isScheduled || !!interviewDate) && !jobsLoading;

  return (
    <Modal show={show} onHide={handleCancel} centered size="lg">
      <Modal.Body style={{ padding: 0, borderRadius: 20, overflow: 'hidden', background: '#fff' }}>

        {/* Header */}
        <div style={{ padding: '24px 28px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h5 style={{ fontWeight: 700, fontSize: 20, margin: '0 0 4px', color: '#111827' }}>
                Mark as {initialStatus}
              </h5>
              <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                {lockJobSelection
                  ? `This candidate applied for this job. Add an optional reason for marking this candidate as ${initialStatus.toLowerCase()}.`
                  : `Select a job and add an optional reason for marking this candidate as ${initialStatus.toLowerCase()}.`}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                width: 34, height: 34, borderRadius: '50%',
                border: '1px solid #E5E7EB', background: '#F9FAFB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, marginLeft: 12,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div style={{ height: '0.5px', background: '#F3F4F6', margin: '0 28px' }} />

        {/* Candidate row */}
        <div style={{ padding: '16px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img
              src={candidateAvatar || NoProfile}
              alt={candidateName}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = NoProfile; }}
              style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#111827' }}>{candidateName}</p>
              {(candidateRole || candidateLocation) && (
                <p style={{ margin: 0, fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                  {[candidateRole, candidateLocation].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Fields */}
        <div style={{ padding: '0 28px 24px' }}>

          {/* ── Job selector (mandatory) ── */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              <Briefcase size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
              {lockJobSelection ? 'Applied Job' : 'Select Job'} <span style={{ color: '#EF4444' }}>*</span>
            </label>

            {lockJobSelection ? (
              <div style={{
                minHeight: 46,
                border: '1.5px solid #D7E4F5',
                borderRadius: 12,
                background: '#F8FBFF',
                color: '#1F2A37',
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 600,
              }}>
                {preselectedJobTitle || jobs.find((job) => job.id === selectedJobId)?.title || 'This job'}
              </div>
            ) : jobsLoading ? (
              <div style={{ height: 46, background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: 12, display: 'flex', alignItems: 'center', paddingLeft: 14 }}>
                <SkeletonLoader inline width={160} height={14} style={{ borderRadius: 6 }} />
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedJobId}
                  onChange={(e) => {
                    setSelectedJobId(e.target.value === '' ? '' : Number(e.target.value));
                    setJobError(false);
                  }}
                  style={{
                    width: '100%',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    border: `1.5px solid ${jobError ? '#EF4444' : selectedJobId ? '#2563EB' : '#E5E7EB'}`,
                    borderRadius: 12,
                    padding: '12px 40px 12px 16px',
                    fontSize: 14,
                    outline: 'none',
                    color: selectedJobId ? '#1F2A37' : '#9CA3AF',
                    background: '#F9FAFB',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">— Select a job position —</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#6B7280', pointerEvents: 'none' }}
                />
              </div>
            )}

            {jobError && !lockJobSelection && (
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#EF4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Please select a job position before updating the status.
              </p>
            )}

            {jobs.length === 0 && !jobsLoading && !lockJobSelection && (
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#F59E0B' }}>
                No active job postings found. Please create a job first.
              </p>
            )}
          </div>

          {/* Interview date picker — only for Scheduled */}
          {isScheduled && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Interview Date &amp; Time <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="datetime-local"
                value={interviewDate}
                min={minDateTime}
                onChange={(e) => setInterviewDate(e.target.value)}
                style={{
                  width: '100%',
                  border: `1.5px solid ${interviewDate ? '#2563EB' : '#E5E7EB'}`,
                  borderRadius: 12,
                  padding: '12px 16px',
                  fontSize: 14,
                  outline: 'none',
                  color: '#1F2A37',
                  background: '#F9FAFB',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              />
              {!interviewDate && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>
                  Please select an interview date and time.
                </p>
              )}
            </div>
          )}

          {/* Reason textarea */}
          <textarea
            value={reason}
            onChange={(e) => { if (e.target.value.length <= 300) setReason(e.target.value); }}
            placeholder="Write your reason (optional)..."
            rows={4}
            maxLength={300}
            style={{
              width: '100%',
              border: '1.5px solid #E5E7EB',
              borderRadius: 12,
              padding: '14px 16px',
              fontSize: 14,
              resize: 'none',
              outline: 'none',
              color: '#1F2A37',
              background: '#F9FAFB',
              lineHeight: 1.6,
              boxSizing: 'border-box',
              paddingBottom: '40px',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#2563EB'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
          />
          <small style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: reason.length >= 290 ? '#EF4444' : '#9CA3AF', pointerEvents: 'none', background: '#F9FAFB', padding: '0 4px' }}>
            {reason.length}/300
          </small>

          {/* Confirm button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !canConfirm}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 24px',
                borderRadius: 12,
                border: 'none',
                background: canConfirm ? '#002781' : '#9CA3AF',
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                cursor: (loading || !canConfirm) ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {loading ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.45)',
                    borderTopColor: '#fff',
                    display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
              {loading ? 'Confirming...' : 'Confirm'}
            </button>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  );
};

export default StatusReasonModal;
